import React, { useEffect, useRef, useContext } from "react"
import { useParams, useLocation } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useQueryClient } from "react-query"
import { toast } from "react-toastify"
import Header from "../shared/Header/Header"
import { FeedSpinner } from "../shared/Spinner"
import { useBand } from "../../hooks/useBand"
import { UserContext } from "../HomeApp"
import { postUserInteraction } from "../../mutations/postUserInteraction"
import { useReferralConversion } from "@hooks/useReferralConversion"
import { useReferralCapture } from "@hooks/useReferralCapture"
import { openAuthDialog } from "../Authentication"
import { mutationRequest } from "../../util/request"
import style from "../shared/PageStyle.module.css"
import UnknownBand from "../bands/UnknownBand"
import BasicTemplate from "./templates/BasicTemplate"
import TimelineTemplate from "./templates/TimelineTemplate"
import ScrollerTemplate from "./templates/ScrollerTemplate"
import BandFooter from "./BandFooter"
import type { Band } from "../../types/bands"

interface BandData {
  band: Band
}

export const BandScreen: React.FC = () => {
  const intervalRef = useRef<NodeJS.Timeout>()
  let { custom_subdomain: bandId } = useParams<{ custom_subdomain: string }>()
  const location = useLocation()
  const { user } = useContext(UserContext)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  // eslint-disable-next-line no-restricted-globals
  const queryParams = new URLSearchParams(location.search)
  const referrer = queryParams.get("referrer") || document.referrer
  const followParam = queryParams.get("follow") === "true"
  const templateOverride = queryParams.get("template")

  // Capture referral when arriving via a tracked link (e.g. a band's own
  // share button, or a user's personal "Share bandNada" link).
  useReferralCapture()

  const {
    status: bandStatus,
    data: bandData,
    error: bandError,
  } = useBand(bandId, null, "is_live,backline")
  const band: Band | undefined = bandData?.band

  useEffect(() => {
    if (!user && followParam && bandId && band?.name) {
      dispatch(
        openAuthDialog({
          bandId,
          modalState: "register",
          explanation: `Join ${band.name}'s local scene to be in the know`,
        }),
      )
    }
  }, [user, followParam, bandId, band?.name, dispatch])

  // Already-logged-in visitor arriving via a tracked "follow" link (e.g. a
  // personal "Share bandNada" link) - auto-follow rather than making them
  // click Follow themselves. No-op (and no toast) for your own band page.
  useEffect(() => {
    if (!user || !followParam || !band?.id || band.id === user.band?.id) return

    mutationRequest({
      options: { method: "POST" },
      path: "/api/v1/user_bands",
      data: { band_id: band.id },
    })
      .then(() => {
        toast.success(`You're now following ${band.name}!`)
        // Refreshes any FollowComponent/FollowButton already on screen (e.g.
        // in the band header) so it flips to "Following" without a reload.
        // FollowComponent nests its identity array as the query's own first
        // element (see FollowComponent.js), so this must be wrapped too for
        // react-query's prefix match to actually hit it.
        queryClient.invalidateQueries([["user_band", user.id, band.id]])
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, followParam, band?.id, band?.name])

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const intervalId = setInterval(() => {
      postUserInteraction(bandId, null, referrer)
    }, 60000)
    intervalRef.current = intervalId
    postUserInteraction(bandId, null, referrer)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [bandId, referrer])

  useReferralConversion({ convertibleType: "Band", convertibleId: band?.id })

  return (
    <div className={style.rootElStyle} style={{ height: "auto", minHeight: "100vh", overflow: "visible" }}>
      <Header user={user} />
      <div className="grid grid-cols-12 w-full">
        {bandStatus === "loading" ? (
          <FeedSpinner />
        ) : bandStatus === "error" ? (
          <UnknownBand error={bandError} bandId={bandId} />
        ) : (
          <>
            {(templateOverride ?? band?.template_name) === "scroller" ? (
              <ScrollerTemplate band={band} />
            ) : (templateOverride ?? band?.template_name) === "timeline" ? (
              <TimelineTemplate band={band} />
            ) : (
              <BasicTemplate band={band} />
            )}
          </>
        )}
      </div>
      <BandFooter band={band} />
    </div>
  )
}
